import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardMedia,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  Badge,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  AddToPhotos as AddToAlbumIcon,
  PhotoSizeSelectActual as SizeIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  getAllImages,
  deleteImage,
  getThumbnailUrl,
  getAllAlbums,
  addImageToAlbum
} from '../services/api';
import FacetedSearch from './FacetedSearch';
import DimensionSelector from './DimensionSelector';
import { groupImagesByBaseName, getRepresentativeImages } from '../utils/imageGrouping';
import { downloadImage } from '../utils/downloadUtils';

const MotionCard = motion(Card);

const Gallery = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  // const [groupedImages, setGroupedImages] = useState({}); // Removed unused state
  const [displayImages, setDisplayImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [selectedAlbums, setSelectedAlbums] = useState([]);

  // Album search state
  const [albumSearchQuery, setAlbumSearchQuery] = useState('');

  // Dimension selector state
  const [hoveredImage, setHoveredImage] = useState(null);
  const [selectedImageVariants, setSelectedImageVariants] = useState({});

  const loadImages = useCallback(async (pageNum = 1, append = false, currentFilters = filters) => {
    try {
      setLoading(true);
      const response = await getAllImages(pageNum, 20, 'date', currentFilters);

      let allImages;
      if (append) {
        allImages = [...images, ...response.images];
        setImages(allImages);
      } else {
        allImages = response.images;
        setImages(allImages);
        setTotalCount(response.total); // Set total count from API response
      }

      // Group images by base filename
      const grouped = groupImagesByBaseName(allImages);

      // Get representative images for display
      const representatives = getRepresentativeImages(grouped);
      setDisplayImages(representatives);

      setHasMore(response.images.length === 20);
      setError(null);
    } catch (err) {
      console.error('Error loading images:', err);
      setError('Failed to load images. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [images, filters]);

  // Load images when filters change
  useEffect(() => {
    setPage(1);
    loadImages(1, false, filters);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load more images when page changes
  useEffect(() => {
    if (page > 1) {
      loadImages(page, true, filters);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteImage = async (imageId, event) => {
    event.stopPropagation();

    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await deleteImage(imageId);

        // Update the images state
        const updatedImages = images.filter(img => img.id !== imageId);
        setImages(updatedImages);

        // Re-group and update display images
        const grouped = groupImagesByBaseName(updatedImages);
        const representatives = getRepresentativeImages(grouped);
        setDisplayImages(representatives);

      } catch (err) {
        console.error('Error deleting image:', err);
        setError('Failed to delete image. Please try again.');
      }
    }
  };

  const handleImageClick = (imageId) => {
    navigate(`/image/${imageId}`);
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleAddToAlbum = async (imageId, event) => {
    event.stopPropagation();
    handleAlbumDialogOpen(imageId);
  };

  const handleAlbumToggle = (albumId) => {
    setSelectedAlbums(prev => {
      if (prev.includes(albumId)) {
        return prev.filter(id => id !== albumId);
      }
      return [...prev, albumId];
    });
  };

  const handleSaveToAlbums = async () => {
    if (!selectedImage || selectedAlbums.length === 0) return;

    try {
      // Add image to each selected album
      await Promise.all(
        selectedAlbums.map(albumId => addImageToAlbum(albumId, selectedImage))
      );

      setAlbumDialogOpen(false);
      setSelectedImage(null);
      setSelectedAlbums([]);
      // Optionally show success message
    } catch (err) {
      console.error('Error adding to albums:', err);
      setError('Failed to add image to albums.');
    }
  };

  // Handle image variant selection
  const handleImageVariantSelect = (baseName, selectedImage) => {
    setSelectedImageVariants(prev => ({
      ...prev,
      [baseName]: selectedImage
    }));
  };

  // Get the currently selected image for a group, or the representative image
  const getCurrentImageForGroup = (imageGroup) => {
    const baseName = imageGroup.baseName;
    return selectedImageVariants[baseName] || imageGroup;
  };

  // Handle image download
  const handleDownloadImage = async (image, event) => {
    event.stopPropagation();
    await downloadImage(image, null, () => {
      setError('Failed to download image. Please try again.');
    });
  };

  // Filter albums based on search query
  const filteredAlbums = useMemo(() => {
    if (!albumSearchQuery.trim()) {
      return albums;
    }

    const query = albumSearchQuery.toLowerCase().trim();
    return albums.filter(album =>
      album.name.toLowerCase().includes(query) ||
      (album.description && album.description.toLowerCase().includes(query))
    );
  }, [albums, albumSearchQuery]);

  // Clear search when dialog opens
  const handleAlbumDialogOpen = (imageId) => {
    setSelectedImage(imageId);
    setAlbumSearchQuery(''); // Clear search when opening

    // Load albums if not already loaded
    if (albums.length === 0) {
      loadAlbums();
    }

    setAlbumDialogOpen(true);
  };

  const loadAlbums = async () => {
    try {
      const response = await getAllAlbums();
      setAlbums(response.albums);
    } catch (err) {
      console.error('Error loading albums:', err);
      setError('Failed to load albums.');
    }
  };

  // Helper function to highlight search matches
  const highlightSearchMatch = (text, query) => {
    if (!query.trim()) return text;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === query.toLowerCase()) {
        return (
          <Box
            key={index}
            component="span"
            sx={{
              backgroundColor: 'rgba(102, 126, 234, 0.3)',
              fontWeight: 'bold',
              padding: '0 2px',
              borderRadius: '2px'
            }}
          >
            {part}
          </Box>
        );
      }
      return part;
    });
  };

  if (loading && images.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress size={60} sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  if (error && images.length === 0) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        {/* Company Logo */}
        <Box sx={{ mb: 3 }}>
          <img
            src="/logo.webp"
            alt="Company Logo"
            style={{
              height: '50px',
              maxWidth: '180px',
              objectFit: 'contain',
            }}
          />
        </Box>

        <Typography
          variant="h4"
          sx={{
            mb: 2,
            color: '#ffffff',
            fontWeight: 600,
          }}
        >
          Your AI Photo Gallery
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          Discover your photos with AI-powered search. Find images by describing what you see,
          search for objects, colors, emotions, or upload an image to find similar ones.
        </Typography>
      </Box>

      {displayImages.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: '#161616',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
            No Images Yet
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Upload your first images to start building your AI-powered gallery
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Faceted Search Sidebar - Fixed Position */}
          <FacetedSearch
            filters={filters}
            onFiltersChange={setFilters}
            resultCount={totalCount}
          />

          {/* Images Grid - Full Width with Left Margin */}
          <Box sx={{ ml: '292px', pr: 2, pl: 1 }}>
            <Grid container spacing={2}>
              {displayImages.map((image, index) => {
                const currentImage = getCurrentImageForGroup(image);
                return (
                <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2.4} key={image.id}>
                <Fade in timeout={300 + index * 50}>
                  <MotionCard
                    whileHover={{
                      scale: 1.03,
                      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                    }}
                    whileTap={{ scale: 0.98 }}
                    sx={{
                      cursor: 'pointer',
                      background: 'rgba(26, 26, 46, 0.8)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={() => handleImageClick(currentImage.id)}
                    onMouseEnter={() => setHoveredImage(image.baseName)}
                    onMouseLeave={() => setHoveredImage(null)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="160"
                        image={getThumbnailUrl(currentImage.thumbnail_path, currentImage.thumbnail_s3_url, currentImage.id) || '/placeholder-image.jpg'}
                        alt={currentImage.description || 'Image'}
                        sx={{
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          }
                        }}
                      />

                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          display: 'flex',
                          gap: 1,
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                          '.MuiCard-root:hover &': {
                            opacity: 1,
                          }
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(102, 126, 234, 0.8)',
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(currentImage.id);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(76, 175, 80, 0.8)',
                            }
                          }}
                          onClick={(e) => handleAddToAlbum(currentImage.id, e)}
                        >
                          <AddToAlbumIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(33, 150, 243, 0.8)',
                            }
                          }}
                          onClick={(e) => handleDownloadImage(currentImage, e)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.8)',
                            }
                          }}
                          onClick={(e) => handleDeleteImage(currentImage.id, e)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Dimension Selector Overlay */}
                      {image.isGrouped && hoveredImage === image.baseName && (
                        <DimensionSelector
                          imageGroup={image.groupVariants}
                          selectedImage={currentImage}
                          onImageSelect={(selectedImage) => handleImageVariantSelect(image.baseName, selectedImage)}
                          onDownloadError={() => setError('Failed to download image. Please try again.')}
                          showInline={true}
                        />
                      )}

                      {/* Multi-size indicator */}
                      {image.isGrouped && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 1
                          }}
                        >
                          <Badge
                            badgeContent={image.variantCount}
                            color="primary"
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.7rem',
                                minWidth: 16,
                                height: 16
                              }
                            }}
                          >
                            <Chip
                              icon={<SizeIcon />}
                              label="sizes"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                fontSize: '0.7rem',
                                height: 20,
                                '& .MuiChip-icon': {
                                  fontSize: '0.8rem',
                                  color: 'white'
                                }
                              }}
                            />
                          </Badge>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {currentImage.description || 'No description available'}
                      </Typography>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {(currentImage.google_vision_labels || currentImage.tags || [])?.slice(0, 3).map((item, idx) => {
                          // Handle both old format (strings) and new AI format (objects)
                          const labelText = typeof item === 'string' ? item : item?.name || item;
                          const confidence = typeof item === 'object' ? item?.confidence : null;

                          return (
                            <Chip
                              key={idx}
                              label={confidence ? `${labelText} (${Math.round(confidence * 100)}%)` : labelText}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: 24,
                                backgroundColor: confidence ? 'rgba(76, 175, 80, 0.2)' : 'rgba(102, 126, 234, 0.2)',
                                color: 'rgba(255, 255, 255, 0.8)',
                                border: confidence ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(102, 126, 234, 0.3)',
                              }}
                            />
                          );
                        })}
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <InfoIcon fontSize="small" />
                        {currentImage.width && currentImage.height ? `${currentImage.width}×${currentImage.height}` : 'AI analyzed'}
                        {image.isGrouped && ` • ${image.variantCount} sizes`}
                        {currentImage.google_vision_faces > 0 && ` • ${currentImage.google_vision_faces} face${currentImage.google_vision_faces > 1 ? 's' : ''}`}
                        {currentImage.label_count > 0 && ` • ${currentImage.label_count} labels`}
                      </Typography>
                    </Box>
                  </MotionCard>
                </Fade>
              </Grid>
                );
              })}
            </Grid>
          </Box>
        </>
      )}

      {images.length > 0 && hasMore && (
        <Box sx={{ ml: '292px', pr: 2, pl: 1, display: 'flex', justifyContent: 'center', mt: 4 }}>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Card
                  onClick={loadMore}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    background: 'rgba(26, 26, 46, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center',
                    minWidth: 200,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: '#667eea' }} />
                  ) : (
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Load More Images
                    </Typography>
                  )}
                </Card>
          </motion.div>
        </Box>
      )}

      {error && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}>
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ maxWidth: 400 }}
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* Album Selection Dialog */}
      <Dialog
        open={albumDialogOpen}
        onClose={() => setAlbumDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle sx={{ color: '#ffffff' }}>Add to Albums</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
            Select albums to add this image to:
          </Typography>

          {/* Search Input */}
          <TextField
            fullWidth
            placeholder="Search albums..."
            value={albumSearchQuery}
            onChange={(e) => setAlbumSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setAlbumSearchQuery('');
                e.target.blur();
              }
            }}
            variant="outlined"
            size="small"
            autoFocus
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                </InputAdornment>
              ),
              endAdornment: albumSearchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setAlbumSearchQuery('')}
                    sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Search Results Indicator */}
          {albumSearchQuery && (
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                mb: 1,
                display: 'block'
              }}
            >
              Showing {filteredAlbums.length} of {albums.length} albums
            </Typography>
          )}
          <List sx={{ width: '100%', minWidth: 300 }}>
            {filteredAlbums.map((album) => (
              <ListItem key={album.id} disablePadding>
                <ListItemButton
                  onClick={() => handleAlbumToggle(album.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <Checkbox
                    checked={selectedAlbums.includes(album.id)}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      '&.Mui-checked': {
                        color: '#667eea',
                      },
                    }}
                  />
                  <ListItemText
                    primary={highlightSearchMatch(album.name, albumSearchQuery)}
                    secondary={`${album.image_count} images`}
                    primaryTypographyProps={{
                      color: '#ffffff'
                    }}
                    secondaryTypographyProps={{
                      color: 'rgba(255, 255, 255, 0.5)'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {albums.length === 0 && (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center', py: 2 }}>
              No albums available. Create an album first.
            </Typography>
          )}

          {albums.length > 0 && filteredAlbums.length === 0 && (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center', py: 2 }}>
              No albums match your search "{albumSearchQuery}".
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlbumDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveToAlbums}
            variant="contained"
            disabled={selectedAlbums.length === 0}
            sx={{
              backgroundColor: '#667eea',
              '&:hover': {
                backgroundColor: '#764ba2',
              },
            }}
          >
            Add to {selectedAlbums.length} Album{selectedAlbums.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Gallery;
